import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';
import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { useForm, useWatch } from 'react-hook-form';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { GButton } from '@gamiphy/loyalty-station-desktop/uikit';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { isAxiosError } from 'axios';
import debounce from 'lodash/debounce';
import { UserInfoFormValues } from './types';
import PhoneField from './PhoneField';
import NameField from './NameField';
import { UserFormSteps } from '../../enums';

const UserInfoForm: React.FC<{
  defaultPhone?: string
  defaultName?: string
  afterSubmit: (data: { name?: string, submittedPhone: string }) => void,
}> = ({
  defaultName,
  defaultPhone,
  afterSubmit,
}) => {
  const [step, setStep] = React.useState<UserFormSteps>(UserFormSteps.Phone);
  const { t } = useTranslation();
  const [userExists, setUserExists] = React.useState(false);
  const [checkingUser, setCheckingUser] = React.useState(false);

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    formState: { isSubmitting },
    setError,
  } = useForm<UserInfoFormValues>({ mode: 'all' });

  React.useEffect(() => {
    reset({
      name: defaultName,
      phone: defaultPhone,
    });
  }, [defaultName, defaultPhone]);

  const enteredPhone = useWatch({
    control,
    name: 'phone',
  });

  const allSteps = Object.values(UserFormSteps);

  const stepsArray = React.useMemo(() => {
    if (userExists) {
      return allSteps.filter((s) => s !== UserFormSteps.Name);
    }
    return allSteps;
  }, [allSteps, userExists]);

  const isFinalStep = React.useMemo(() => step === stepsArray[stepsArray.length - 1], [step, stepsArray]);

  const content = React.useMemo(() => {
    switch (step) {
      case UserFormSteps.Phone:
        return <PhoneField control={control} />;
      case UserFormSteps.Name:
        return <NameField control={control} />;
      default:
        return undefined;
    }
  }, [control, step]);

  const title = React.useMemo(() => {
    switch (step) {
      case UserFormSteps.Phone:
        return (
          <Trans
            i18nKey="user.welcome.hi"
            defaults="Hi there!"
          />
        );
      case UserFormSteps.Name:
        return (
          <Trans
            i18nKey="user.welcome.whoIsGoining"
            defaults="Who's Joining Us Today?"
          />
        );
      default:
        return undefined;
    }
  }, [step]);

  const description = React.useMemo(() => {
    switch (step) {
      case UserFormSteps.Phone:
        return (
          <Trans
            i18nKey="user.welcome.enterPhone"
            defaults="Please enter your Phone Number, and we'll send you a code to log in."
          />
        );
      case UserFormSteps.Name:
        return (
          <Trans
            i18nKey="user.welcome.enterName"
            defaults="We’d love to know your name! Could you kindly share it with us?"
          />
        );
      default:
        return undefined;
    }
  }, [step]);

  const getUserByPhone = React.useCallback(debounce(async (phone: string) => {
    try {
      const response = await UserStore.api.usersControllerUserExists(AppStore.app.id, phone);
      setUserExists(response.data);
    } finally {
      setCheckingUser(false);
    }
  }, 300), [
    AppStore.app,
    step,
    UserStore.api,
  ]);

  React.useEffect(() => {
    if (enteredPhone) {
      setCheckingUser(true);
      void getUserByPhone(`+55${enteredPhone}`);
    }
  }, [enteredPhone]);

  const onSubmit = React.useCallback(handleSubmit(
    async (data) => {
      try {
        const phoneWithDialingCode = `+55${data.phone}`;
        if (UserStore.isLoggedIn) {
          await UserStore.updatePhone(phoneWithDialingCode);
        } else {
          await UserStore.api.usersVerificationsControllerSendOtp(AppStore.app.id, {
            phone: phoneWithDialingCode,
            name: data.name,
          });
        }
        afterSubmit({
          name: data.name,
          submittedPhone: phoneWithDialingCode,
        });
      } catch (e) {
        if (isAxiosError(e)) {
          switch (e.response?.status) {
            case 409:
              setError('phone', { message: t('cashbackPopup.phone.exists', 'Phone number linked with another account.') });
              break;
            default:
              throw e;
          }
        }
      }
    },
  ), [
    UserStore.api,
    UserStore.isLoggedIn,
  ]);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Typography fontSize="2rem" fontWeight={600}>
        {title}
      </Typography>
      <Typography fontSize="1.6rem" fontWeight={400} color="#4F4F4F">
        {description}
      </Typography>
      {content}
      {isFinalStep ? (
        <GButton
          variant="contained"
          color="primary"
          type="submit"
          loading={isSubmitting}
        >
          <Trans i18nKey="shared.verify" defaults="Verify" />
        </GButton>
      ) : (
        <GButton
          variant="contained"
          color="primary"
          type="button"
          loading={checkingUser}
          onClick={async (e) => {
            e.preventDefault();
            const isStepValid = await trigger();
            if (isStepValid) {
              setStep((prevStep) => stepsArray[stepsArray.indexOf(prevStep) + 1]);
            }
          }}
        >
          <Trans i18nKey="continue" defaults="Continue" />
        </GButton>
      )}
    </Box>
  );
};

export default observer(UserInfoForm);
